import { RiArrowDownSLine } from '@remixicon/react';
import type { ComponentProps, ReactNode } from 'react';

import { type Size, cn } from '@playbook/utils';

import css from './styles.module.css';

export type DropdownTriggerSize = Extract<Size, 'sm' | 'md'>;

const DefaultDropdownTriggerSize: DropdownTriggerSize = 'md' as const;

type DropdownTriggerEventHandlers = {
	[K in keyof ComponentProps<'button'> as K extends `on${string}`
		? K
		: never]?: ComponentProps<'button'>[K];
};

interface DropdownTriggerDataAttributes {
	[key: `data-${string}`]: string | number | boolean | undefined;
}

/**
 *	ARIA is admitted the way `data-*` is, on the same argument
 *	`atoms/pagination-button` makes: a control that opens something cannot know
 *	what it opened, so whatever arranges the two writes `aria-expanded`,
 *	`aria-haspopup` and `aria-labelledby` from outside.
 */
interface DropdownTriggerAriaAttributes {
	[key: `aria-${string}`]: string | number | boolean | undefined;
}

interface DropdownTriggerProperties
	extends
		DropdownTriggerEventHandlers,
		DropdownTriggerDataAttributes,
		DropdownTriggerAriaAttributes {
	size?: DropdownTriggerSize;
	disabled?: boolean;

	/**
	 *	Owned rather than refused, the way `atoms/inputs/text-area` owns it: this
	 *	is a form control, and the label above it has to have something to point
	 *	`htmlFor` at. It is also the `id`, as it is there.
	 */
	name: string;

	/**
	 *	Shown until something is chosen. Required, since what to choose is the
	 *	caller's to say: no single word fits every list.
	 */
	placeholder: string;

	/** The chosen option, already resolved to the words it shows. */
	value?: string;

	/** The `id` of the popover this opens. See `modules/dropdown`. */
	popoverTarget?: string;

	/**
	 *	Figma's `left-icon` on `.text input`, the calendar a date picker opens
	 *	with. Sized and coloured by the same rule as the chevron.
	 */
	leadingIcon?: ReactNode;
}

/**
 *	The closed half of a dropdown: what you click to open the list, and what
 *	shows the choice once it is made.
 *
 *	A `<button>`, not an `<input>`, which is why it is its own atom rather than
 *	a variant of `atoms/inputs/text-input` — the two draw the same box from the
 *	same private Figma node (`.text input`), but one is typed into and the other
 *	is pressed.
 *
 *	It does not open anything. The list, where it goes and whether it is showing
 *	belong to `modules/dropdown`, which is the only thing that can know
 *	about both halves at once.
 */
export function DropdownTrigger({
	size = DefaultDropdownTriggerSize,
	disabled = false,

	name,
	placeholder,
	value,

	popoverTarget,

	leadingIcon,

	...props
}: DropdownTriggerProperties): ReactNode {
	/**
	 *	`size` is not indexed the way a variant would be — only `small`
	 *	re-declares anything, as on the button and the menu item. `empty` is the
	 *	state Figma draws for a dropdown nothing has been chosen in yet;
	 *	`selected` is the base class.
	 */
	const className: string = cn(
		css.dropdownTrigger,
		size === 'sm' && css.sm,
		!value && css.empty
	);

	/**
	 *	`compositions/input-group` writes `variant` onto every segment that is
	 *	not a text input, which reaches this through a dropdown or date picker
	 *	and would otherwise land on the `<button>` as a stray attribute.
	 */
	delete (props as { variant?: unknown }).variant;

	return (
		<button
			{...props}
			type="button"
			id={name}
			disabled={disabled}
			popoverTarget={popoverTarget}
			data-size={size}
			data-filled={Boolean(value)}
			className={className}>
			{leadingIcon}
			<span className={css.value}>{value ?? placeholder}</span>
			<RiArrowDownSLine />
		</button>
	);
}
